import React from 'react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Calendar, Plus, Upload, Download, ChevronLeft, ChevronRight, ListFilter, CalendarDays, LayoutTemplate, Expand, Users, Undo, Redo } from 'lucide-react';
import { CalendarViewMode } from '../types';
import { useLanguage } from '@/contexts/LanguageContext';
import { capitalizeFirstLetter } from '@/lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import ViewModeMenu from './ViewModeMenu';
import GuardSelect from './GuardSelect';
import { useIsMobile } from '@/hooks/use-mobile';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface CalendarToolbarProps {
  isAdmin: boolean;
  viewMode: CalendarViewMode;
  currentDate: Date;
  onAddEvent?: () => void;
  onExport?: () => void;
  onBulkUpload?: () => void;
  onNavigatePrevious: () => void;
  onNavigateNext: () => void;
  onNavigatePreviousDay?: () => void;
  onNavigateNextDay?: () => void;
  onGoToToday: () => void;
  onViewModeChange: (mode: CalendarViewMode) => void;
  isExporting: boolean;
  guards?: any[];
  selectedGuardId?: string;
  handleGuardSelect: (guardId: string) => void;
  onTemplatesClick?: () => void;
  onLoadMoreShifts?: () => void;
  isLoadingMoreShifts?: boolean;
  viewType?: 'standard' | 'per-job';
  onViewTypeChange?: (type: 'standard' | 'per-job') => void;
  hasAdminLikePermissions?: boolean;
  currentWeekStart?: Date; // Add this prop
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
}

const CalendarToolbar: React.FC<CalendarToolbarProps> = ({
  isAdmin,
  viewMode,
  currentDate,
  onAddEvent,
  onExport,
  onBulkUpload,
  onNavigatePrevious,
  onNavigateNext,
  onNavigatePreviousDay,
  onNavigateNextDay,
  onGoToToday,
  onViewModeChange,
  isExporting,
  guards,
  selectedGuardId,
  handleGuardSelect,
  onTemplatesClick,
  onLoadMoreShifts,
  isLoadingMoreShifts = false,
  viewType = 'standard',
  onViewTypeChange,
  hasAdminLikePermissions = false,
  currentWeekStart,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false
}) => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  
  // Use hasAdminLikePermissions instead of just isAdmin
  const canManage = hasAdminLikePermissions;
  
  const renderViewModeName = () => {
    switch (viewMode) {
      case 'day':
        return t("day") || "Day";
      case 'week':
        return t("week") || "Week";
      case 'month':
        return t("month") || "Month";
      case 'custom':
        return t("custom") || "Custom";
      case 'total-work':
        return t("totalWork") || "Total Work";
      default:
        return capitalizeFirstLetter(viewMode);
    }
  };
  
  return (
    <div className="flex flex-col space-y-3 pb-2 md:space-y-0 md:flex-row justify-between w-full">
      <div className="flex items-center gap-3 flex-wrap">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onNavigatePrevious}
          className="h-8 text-xs sm:text-sm border-dashed"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="sr-only sm:not-sr-only sm:ml-1">
            {t("previous") || "Previous"}
          </span>
        </Button>
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onGoToToday}
          className="h-8 text-xs sm:text-sm whitespace-nowrap"
        >
          {t("today") || "Today"}
        </Button>
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onNavigateNext}
          className="h-8 text-xs sm:text-sm border-dashed"
        >
          <span className="sr-only sm:not-sr-only sm:mr-1">
            {t("next") || "Next"}
          </span>
          <ChevronRight className="h-4 w-4" />
        </Button>
        
        {/* Undo/Redo buttons */}
        {canManage && onUndo && onRedo && (
          <div className="flex items-center gap-1 ml-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onUndo}
                    disabled={!canUndo}
                    className="h-8 w-8 p-0"
                  >
                    <Undo className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("undo") || "Undo"} (Ctrl+Z)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onRedo}
                    disabled={!canRedo}
                    className="h-8 w-8 p-0"
                  >
                    <Redo className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("redo") || "Redo"} (Ctrl+Shift+Z)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
        
        {viewMode !== 'total-work' && onLoadMoreShifts && (
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMoreShifts}
            disabled={isLoadingMoreShifts}
            className="h-8 text-xs sm:text-sm"
          >
            {isLoadingMoreShifts ? (
              <>
                <div className="h-3.5 w-3.5 mr-1.5 animate-spin rounded-full border-2 border-gray-300 border-t-primary"></div>
                <span>{t("loading") || "Loading..."}</span>
              </>
            ) : (
              <>
                <Expand className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">{t("loadMoreShifts") || "Load More Shifts"}</span>
              </>
            )}
          </Button>
        )}
        
        <div className="hidden md:flex items-center ml-2">
          <Calendar className="h-4 w-4 mr-1 text-primary" />
          <span className="font-medium text-sm">
            {format(currentDate, 'MMMM yyyy')}
          </span>
        </div>
      </div>
      
      <div className="flex items-center gap-3 w-full flex-wrap justify-between md:justify-end md:w-auto">
        <div className="flex md:hidden items-center">
          <Calendar className="h-4 w-4 mr-1 text-primary" />
          <span className="font-medium text-sm">
            {format(currentDate, 'MMM yyyy')}
          </span>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          {/* View Type Toggle - only show for non-total-work views */}
          {canManage && viewMode !== 'total-work' && onViewTypeChange && (
            <div className="flex items-center gap-1 border rounded-md p-1">
              <Button
                variant={viewType === 'standard' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onViewTypeChange('standard')}
                className="h-6 text-xs px-2"
              >
                {t("standard") || "Standard"}
              </Button>
              <Button
                variant={viewType === 'per-job' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onViewTypeChange('per-job')}
                className="h-6 text-xs px-2"
              >
                <Users className="h-3 w-3 mr-1" />
                {t("perJob") || "Per Job"}
              </Button>
            </div>
          )}

          {canManage && (
            <Button
              variant="outline"
              size="sm"
              onClick={onTemplatesClick}
              className="h-8 text-xs sm:text-sm hidden sm:flex"
            >
              <LayoutTemplate className="h-3.5 w-3.5 mr-1" />
              <span className="hidden md:inline">{t("templatesNextScheduleViews") || "Templates next schedule views"}</span>
            </Button>
          )}
          
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                className="h-8 text-xs sm:text-sm"
              >
                <CalendarDays className="h-3.5 w-3.5 mr-1" />
                <span className="hidden sm:inline">{renderViewModeName()}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-52 p-2" align="end">
              <ViewModeMenu 
                onSelect={onViewModeChange}
                currentMode={viewMode}
              />
            </PopoverContent>
          </Popover>
          
          {/* Only show guard selector to users with admin-like permissions when in total-work view */}
          {canManage && viewMode === 'total-work' && guards && (
            <GuardSelect 
              guards={guards}
              selectedGuardId={selectedGuardId}
              onSelect={handleGuardSelect}
            />
          )}
          
          {canManage && viewMode !== 'total-work' && (
            <>
              {onAddEvent && (
                <Button 
                  size="sm"
                  className="h-8 text-xs sm:text-sm"
                  onClick={onAddEvent}
                >
                  <Plus className="h-3.5 w-3.5 sm:mr-1" />
                  <span className="hidden sm:inline">{t("addShift") || "Add Shift"}</span>
                </Button>
              )}
              
              {onBulkUpload && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onBulkUpload}
                  className="h-8 text-xs sm:text-sm hidden sm:flex"
                >
                  <Upload className="h-3.5 w-3.5 sm:mr-1" />
                  <span className="hidden md:inline">{t("bulkUpload") || "Bulk Upload"}</span>
                </Button>
              )}
              
              {onExport && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onExport}
                  className="h-8 text-xs sm:text-sm hidden sm:flex"
                  disabled={isExporting}
                >
                  <Download className="h-3.5 w-3.5 sm:mr-1" />
                  <span className="hidden md:inline">
                    {isExporting ? (t("exporting") || "Exporting...") : (t("export") || "Export")}
                  </span>
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarToolbar;
